import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  type BalanceSeries,
  type CasinoLeaderboardEntry,
  type CasinoSnapshot,
  type CreateRoomResult,
  type GameType,
  type RoomState,
  type StatsWindow,
  isGameType,
  pocketColor,
} from '@tv-casino/shared';
import { CardRow } from '../components/CardView.js';
import { CrapsDice } from '../components/CrapsDice.js';
import { DeckShoe } from '../components/DeckShoe.js';
import { GamePollBoard } from '../components/GamePollBoard.js';
import { PartyPill } from '../components/PartyStrip.js';
import { CrapsTable } from '../components/CrapsTable.js';
import { RouletteTable } from '../components/RouletteTable.js';
import { RouletteWheel } from '../components/RouletteWheel.js';
import { KenoTable } from '../components/KenoTable.js';
import { fetchBalanceTimeline, fetchLeaderboard, lookupCasino } from '../lib/casinoApi.js';
import { themeStyle } from '../lib/themeStyle.js';
import { MemberMarket } from '../components/MemberMarket.js';
import { WindowPicker } from '../components/WindowPicker.js';
import { parseStatsWindow } from '@tv-casino/viewmodels/components/windowPickerModel.js';
import { SeatHuesContext, seatHues } from '../lib/playerColors.js';
import { ProfileBadge } from '../components/ProfileBadge.js';
import { getSocket, request } from '../lib/socket.js';
import { casinoDisplayIdentity, displayIdentity, statsWindowPref } from '../lib/storage.js';
import { applePlatform } from '../lib/platform.js';
import { useSenderCast } from '../lib/useGoogleCast.js';
import { useViewportSize } from '../lib/useViewport.js';
import { CalloutCaption } from '../components/CalloutCaption.js';
import { CastButton } from '../components/CastButton.js';
import { CouchIcon } from '../components/CouchIcon.js';
import { GameSplash } from '../components/GameSplash.js';
import { GAME_SPLASH_MS, gameSplashVM } from '@tv-casino/viewmodels/components/gameSplashModel.js';
import { SoundButton } from '../components/SoundButton.js';
import { play, setSoundScope, setVolumes } from '../lib/sound.js';
import {
  PAYOUT_CUE_MS,
  useCrapsCues,
  useRouletteCues,
  useTableAmbience,
  useTableCues,
} from '../lib/tableSound.js';
import { useDealerCallouts } from '../lib/useDealerCallouts.js';
import { useGameSplash } from '@tv-casino/viewmodels/lib/useGameSplash.js';
import { useWakeLock } from '../lib/useWakeLock.js';
import { useCrapsRoomReading } from '@tv-casino/viewmodels/components/crapsThrow.js';
import {
  hostModel,
  LEADERBOARD_PAGE_MS,
  LEADERBOARD_PAGE_SIZE,
  LEADERBOARD_STAGE_PAGE_SIZE,
  leaderboardPage,
  type HostVM,
} from '@tv-casino/viewmodels/routes/hostModel.js';
import { mirrorLayout } from '@tv-casino/viewmodels/routes/mirrorLayout.js';
import type { SeatWin, WinHeadline } from '@tv-casino/viewmodels/routes/winModel.js';
import styles from './Host.module.css';

/** How a Host instance knows which table to open: URL boot, or a cast hand. */
export interface HostBoot {
  game: GameType;
  casino: string | null;
  table: string | null;
  handoff: { code: string; displayToken: string } | null;
}

type RouletteVM = NonNullable<HostVM['roulette']>;
type CrapsVM = NonNullable<HostVM['craps']>;

/**
 * The roulette half of the TV: the wheel stands to the left of the betting
 * layout, like the wheel does beside the felt on a real table, with every
 * seat's chips sitting where they were bet. The dolly marks the winning
 * number for the payout beat, exactly as the craps felt flashes its winners.
 */
function RouletteFelt({ vm, endsAt }: { vm: RouletteVM; endsAt: number | null }) {
  return (
    <div className={styles.rouletteFelt}>
      <div className={styles.wheelSide}>
        <RouletteWheel
          size="tv"
          wheelOrder={vm.wheelOrder}
          landedIndex={vm.landedIndex}
          pocket={vm.pocket}
          pocketColor={vm.pocketColor}
          resultLine={vm.resultLine}
          endsAt={endsAt}
        />
      </div>
      <div className={styles.layoutSide}>
        <RouletteTable
          wheel={vm.wheel}
          pocket={vm.pocketValue}
          stacks={vm.stacks}
          marks={vm.marks}
          size="tv"
        />
      </div>
    </div>
  );
}

/**
 * The craps felt: the puck, the dome and the stickman's call. The dome is
 * the shared roller — the TV and the phone each feed it the published roll
 * counter and get the same throw, so the picture can never disagree with
 * the money. It stands to the left of the layout, as the wheel does beside
 * the roulette felt, so the layout gets the full height of the table.
 */
function CrapsFelt({ vm }: { vm: CrapsVM }) {
  return (
    <div className={styles.crapsFelt}>
      <div className={styles.crapsHead}>
        <span className={[styles.puck, vm.puckOn ? styles.puckOn : ''].filter(Boolean).join(' ')}>
          {vm.puckLabel}
        </span>
        <CrapsDice
          size="tv"
          rollNumber={vm.rollNumber}
          dice={vm.dice}
          caption={vm.resultLine ?? vm.callLine}
        />
        {vm.shooterName && (
          <span className={styles.crapsShooter}>
            {vm.shooterName} shooting · roll {vm.rollNumber}
          </span>
        )}
      </div>
      <div className={styles.layoutSide}>
        <CrapsTable
          rules={vm.rules}
          point={vm.point}
          stacks={vm.stacks}
          marks={vm.marks}
          size="tv"
        />
      </div>
    </div>
  );
}

/**
 * The felt is laid out at the size the design asks for, then scaled down
 * until it fits the room the table area has left. A TV has no scrollbar and
 * nobody to reach it, so shrinking the picture is the only way to keep a
 * whole table on screen.
 */
function Felt({
  children,
  fill = false,
}: {
  children: ReactNode;
  /**
   * Lay the stage out wider by the inverse of its scale, so a picture that
   * shrinks to fit the height still spans the room edge to edge.
   */
  fill?: boolean;
}) {
  const box = useRef<HTMLDivElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const room = box.current;
    const drawn = stage.current;
    if (!room || !drawn) return;
    // A transform never feeds back into layout, so the stage's own box still
    // reads its natural height while it is scaled — no measuring loop.
    const fit = () => {
      const have = room.clientHeight;
      const want = drawn.offsetHeight;
      // A felt with a floor on its width (roulette, beside its board)
      // overflows a narrow room rather than crushing its cells, so the
      // picture is scaled back in across as well as down.
      const haveW = drawn.offsetWidth;
      const wantW = Math.max(
        haveW,
        ...Array.from(drawn.children, (c) => (c instanceof HTMLElement ? c.offsetWidth : 0)),
      );
      const byHeight = have > 0 && want > 0 ? have / want : 1;
      const byWidth = haveW > 0 ? haveW / wantW : 1;
      setScale(Math.min(1, byHeight, byWidth));
    };
    fit();
    if (typeof ResizeObserver === 'undefined') return;
    const watch = new ResizeObserver(fit);
    watch.observe(room);
    watch.observe(drawn);
    return () => watch.disconnect();
  }, []);

  return (
    <div className={styles.dealer} ref={box}>
      <div
        className={styles.dealerFit}
        ref={stage}
        style={{ transform: `scale(${scale})`, width: fill ? `${100 / scale}%` : undefined }}
      >
        {children}
      </div>
    </div>
  );
}

const colorClass = (tone: string): string =>
  tone === 'red'
    ? styles.pocketRed ?? ''
    : tone === 'black'
      ? styles.pocketBlack ?? ''
      : styles.pocketGreen ?? '';

/** The fanfare lands just after the chips are heard moving. */
const WIN_CUE_MS = PAYOUT_CUE_MS + 260;

/**
 * A big win gets its fanfare on top of the ordinary payout cue. The first
 * settle the TV sees only primes, like the other cues: a screen that pairs
 * during a payout should not cheer for a hand nobody watched on it.
 */
function useWinCues(ready: boolean, headline: WinHeadline | null): void {
  const key = headline?.key ?? null;
  const tier = headline?.tier ?? null;
  const primed = useRef(false);
  const was = useRef<string | null>(null);

  useEffect(() => {
    if (!ready) return;
    const first = !primed.current;
    primed.current = true;
    if (key === was.current) return;
    was.current = key;
    if (first || !tier) return;
    const timer = window.setTimeout(
      () => play(tier === 'jackpot' ? 'jackpot' : 'bigWin'),
      WIN_CUE_MS,
    );
    return () => window.clearTimeout(timer);
  }, [ready, key, tier]);
}

/** Coins per burst, by how big the win is. */
const BURST_COINS: Record<SeatWin['tier'], number> = { win: 8, big: 14, jackpot: 20 };

/**
 * A seat's win: a ring of light round the plate, coins thrown out of the bet
 * spot, and the amount rising off the top. Keyed on the win, so it plays once
 * per settle. The seat's mark already says the number, so this is hidden from
 * assistive tech.
 */
function WinBurst({ win }: { win: SeatWin }) {
  const coins = BURST_COINS[win.tier];
  return (
    <div
      className={[styles.winBurst, styles[`win_${win.tier}`] ?? ''].filter(Boolean).join(' ')}
      data-win-tier={win.tier}
      aria-hidden="true"
    >
      <span className={styles.winGlow} />
      {Array.from({ length: coins }, (_, i) => (
        <i
          key={i}
          className={styles.coin}
          style={
            {
              '--coin-a': `${(360 / coins) * i + ((i * 23) % 17)}deg`,
              '--coin-d': `${46 + ((i * 37) % 44)}px`,
              '--coin-t': `${(i % 4) * 45}ms`,
            } as CSSProperties
          }
        />
      ))}
      <span className={styles.winFloat}>{win.label}</span>
    </div>
  );
}

/** How many coins fall across the felt on a jackpot. */
const RAIN_COINS = 32;

/** The banner the felt stops for: title, amount, who. Coins rain on a jackpot. */
function WinBanner({ headline }: { headline: WinHeadline }) {
  const jackpot = headline.tier === 'jackpot';
  return (
    <div
      className={[styles.winBanner, jackpot ? styles.winBannerJackpot : ''].filter(Boolean).join(' ')}
      role="status"
    >
      {jackpot && (
        <div className={styles.coinRain} aria-hidden="true">
          {Array.from({ length: RAIN_COINS }, (_, i) => (
            <i
              key={i}
              className={styles.rainCoin}
              style={
                {
                  '--rain-x': `${(i * 37) % 100}%`,
                  '--rain-t': `${(i * 113) % 1_600}ms`,
                  '--rain-s': `${0.7 + ((i * 7) % 6) / 10}`,
                } as CSSProperties
              }
            />
          ))}
        </div>
      )}
      <div className={styles.winBannerCard}>
        <span className={styles.winBannerTitle}>{headline.title}</span>
        <span className={styles.winBannerAmount}>{headline.label}</span>
        <span className={styles.winBannerName}>
          {headline.name}
          {headline.others > 0 ? ` · ${headline.others} more big win${headline.others === 1 ? '' : 's'}` : ''}
        </span>
      </div>
    </div>
  );
}

/**
 * The TV's own levels: both of the host's volume sliders, which ride in with
 * the room state. The floor bed itself is `useTableAmbience`, shared with the
 * phones — only how loud it sits is the host's to set from here.
 */
function useTvVolumes(state: RoomState | null): void {
  const soundVolume = state?.soundVolume ?? 100;
  const ambienceVolume = state?.ambienceVolume ?? 100;
  useEffect(() => setVolumes(soundVolume, ambienceVolume), [soundVolume, ambienceVolume]);
}

/** The pockets the wheel has landed, oldest on the left. */
function PocketRoad({ road, className }: { road: RouletteVM['pocketRoad']; className?: string }) {
  return (
    <ol
      className={[styles.pocketRoad, className].filter(Boolean).join(' ')}
      aria-label="Past pockets, oldest first"
    >
      {road.map((bead, i) => (
        <li key={`${i}-${bead.pocket}`} className={`${styles.bead} ${colorClass(bead.color)}`} title={bead.pocket}>
          {bead.pocket}
        </li>
      ))}
    </ol>
  );
}

/**
 * The trends read in the pairs a player bets them — red against black, odd
 * against even, low against high — one split pill a pair. Whatever neither
 * side took is the zero, so it sits as a green sliver between them.
 */
const TREND_PAIRS = [
  ['Red', 'Black'],
  ['Odd', 'Even'],
  ['1–18', '19–36'],
] as const;

/**
 * The hot/cold board. A wheel has no memory, so this is a record of what has
 * happened and nothing more — the strip says as much rather than letting the
 * table read a due number into it. It stands beside the felt with the pocket
 * road at its head: the felt runs out of height long before it runs out of
 * width, so the night's history takes the width and leaves the height alone.
 */
function PocketBoard({
  board,
  road,
  count,
}: {
  board: NonNullable<RouletteVM['board']>;
  road: RouletteVM['pocketRoad'];
  /** This spin's number, printed at the head of the history it extends. */
  count: string | null;
}) {
  const trend = (label: string) => board.trends.find((t) => t.label === label);
  const zero = trend('Zero');
  const end = (label: string, tallies: typeof board.hot) => (
    <div className={styles.boardEnd}>
      <span className={styles.boardTitle}>{label}</span>
      <ol className={styles.boardPockets} aria-label={`${label} pockets`}>
        {tallies.map((t) => (
          <li key={t.pocket} className={`${styles.boardPocket} ${colorClass(t.color)}`}>
            <span>{t.pocket}</span>
            <span className={styles.boardCount}>{t.count}</span>
          </li>
        ))}
      </ol>
    </div>
  );

  return (
    <section className={styles.board} aria-label="Hot and cold pockets">
      <div className={styles.boardHead}>
        <span className={styles.boardTitle}>{`Last ${board.spins} spins`}</span>
        {count && <span className={styles.historyCount}>{count}</span>}
      </div>
      {road.length > 0 && <PocketRoad road={road} className={styles.boardRoad} />}
      {end('Hot', board.hot)}
      {end('Cold', board.cold)}
      <ul className={styles.boardTrends} aria-label="Even-money splits">
        {TREND_PAIRS.map(([a, b]) => {
          const left = trend(a);
          const right = trend(b);
          if (!left || !right) return null;
          const zeros = Math.max(0, board.spins - left.count - right.count);
          return (
            <li key={a} className={a === 'Red' ? `${styles.trend} ${styles.trendColor}` : styles.trend}>
              <span className={styles.trendSplit} aria-hidden="true">
                <span style={{ flexGrow: left.count }} />
                <span className={styles.trendZero} style={{ flexGrow: zeros }} />
                <span style={{ flexGrow: right.count }} />
              </span>
              <span className={styles.trendSide}>
                {a} <span className={styles.trendPct}>{`${left.pct}%`}</span>
              </span>
              <span className={styles.trendSide}>
                <span className={styles.trendPct}>{`${right.pct}%`}</span> {b}
              </span>
            </li>
          );
        })}
        {zero && (
          <li className={styles.trendZeroLine}>
            Zero <span className={styles.trendPct}>{`${zero.pct}%`}</span>
          </li>
        )}
      </ul>
    </section>
  );
}

/**
 * Nobody gets a cycling board they did not ask for. Read once at mount so the
 * carousel cannot end up half-still if the preference flips mid-night.
 */
const useStill = (): boolean =>
  useState(
    () =>
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  )[0];

function LobbyBoard({
  board,
  stage = false,
  statsWindow,
  onStatsWindow,
}: {
  board: NonNullable<HostVM['lobbyBoard']>;
  /** Empty table or host-forced: the board owns the stage, so page larger. */
  stage?: boolean;
  /** The window the board's cards quote; the picker hides without a setter. */
  statsWindow?: StatsWindow;
  /** Cut the board to a new window; the route owns the refetch. */
  onStatsWindow?: (window: StatsWindow) => void;
}) {
  const still = useStill();
  const [tick, setTick] = useState(0);
  const pageSize = stage || board.forced ? LEADERBOARD_STAGE_PAGE_SIZE : LEADERBOARD_PAGE_SIZE;
  const page = leaderboardPage(board.leaders, tick, pageSize);

  useEffect(() => {
    if (still || !page.cycling) return;
    const t = window.setInterval(() => setTick((n) => n + 1), LEADERBOARD_PAGE_MS);
    return () => window.clearInterval(t);
  }, [still, page.cycling]);

  return (
    <aside
      className={[
        styles.houseBoard,
        board.forced || stage ? styles.houseBoardForced : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label={board.forced ? 'Leaderboard' : 'Tonight at the casino'}
    >
      <div className={styles.boardHead}>
        <p className={styles.boardTitle}>
          {board.forced ? 'Leaderboard' : `Tonight at ${board.casinoName}`}
        </p>
        {statsWindow && onStatsWindow && (
          <WindowPicker
            value={statsWindow}
            onChange={onStatsWindow}
            label="Board window"
          />
        )}
      </div>

      {board.tables.length > 0 && !board.forced && (
        <ul className={styles.boardTables}>
          {board.tables.map((table) => (
            <li
              key={table.id}
              className={[
                styles.boardTable,
                table.live ? styles.boardTableActive : '',
                table.closed ? styles.boardTableClosed : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <span>
                {table.name} · {table.gameLabel}
              </span>
              <span className={styles.boardNote}>{table.note}</span>
            </li>
          ))}
        </ul>
      )}

      {board.forced ? (
        board.emptyLine ? (
          <p className={styles.dim}>{board.emptyLine}</p>
        ) : (
          <ol className={styles.boardDesks}>
            {board.desks.map((desk, i) => (
              <li key={desk.name + i} className={styles.boardDesk}>
                <span className={styles.boardDeskRank}>{i + 1}</span>
                <div className={styles.boardDeskBody}>
                  <MemberMarket vm={desk} quoteOnly compact />
                </div>
              </li>
            ))}
          </ol>
        )
      ) : (
        <>
          <p className={styles.boardTitle}>Leaderboard</p>
          {board.emptyLine ? (
            <p className={styles.dim}>{board.emptyLine}</p>
          ) : (
            <>
              <ol
                key={page.page}
                className={styles.boardLeaders}
                aria-live="polite"
                aria-atomic="true"
              >
                {page.visible.map((leader) => (
                  <li key={leader.memberId} className={styles.boardLeader}>
                    <span className={styles.boardRank}>{leader.rank}</span>
                    <span className={styles.boardName}>
                      {leader.accentHex && (
                        <span
                          className={styles.boardDot}
                          style={{ '--accent': leader.accentHex } as CSSProperties}
                          aria-hidden="true"
                        />
                      )}
                      {leader.name}
                      {leader.hasProfile && <ProfileBadge />}
                    </span>
                    <span
                      className={[
                        styles.boardNet,
                        leader.tone === 'win' ? styles.boardNetWin : '',
                        leader.tone === 'loss' ? styles.boardNetLoss : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {leader.netLabel}
                    </span>
                    <span className={styles.boardHands}>{leader.handsLabel}</span>
                    {(leader.streakLabel || leader.wageredLabel || leader.extremesLabel) && (
                      <span className={styles.boardMarks}>
                        {[leader.streakLabel, leader.wageredLabel, leader.extremesLabel]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    )}
                  </li>
                ))}
              </ol>
              {page.cycling && (
                <div
                  className={styles.boardPages}
                  aria-label={`Leaderboard page ${page.page + 1} of ${page.pageCount}`}
                >
                  {Array.from({ length: page.pageCount }, (_, i) => (
                    <span
                      key={i}
                      className={[styles.boardPageDot, i === page.page ? styles.boardPageDotOn : '']
                        .filter(Boolean)
                        .join(' ')}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}
    </aside>
  );
}

/**
 * Game is memoized at module level: React may mount twice (StrictMode) and
 * the first pass strips the query. Casino and room handoff are persisted
 * before that strip — a remount still pairs instead of opening a new table.
 */
let preferredGameOnce: GameType | null = null;

const readHostBoot = (): HostBoot => {
  const params = new URLSearchParams(window.location.search);
  const gameParam = params.get('game');
  const casinoParam = params.get('casino');
  const tableParam = params.get('table');
  const code = params.get('code');
  const displayToken = params.get('display');

  if (preferredGameOnce === null) {
    preferredGameOnce = isGameType(gameParam) ? gameParam : 'blackjack';
  }

  // URL wins over leftover pairing from a previous visit in this tab.
  if (tableParam) {
    casinoDisplayIdentity.set(
      casinoParam ? casinoParam.toUpperCase() : null,
      tableParam.toUpperCase(),
    );
    displayIdentity.clear();
  } else if (casinoParam) {
    casinoDisplayIdentity.set(casinoParam.toUpperCase(), null);
    displayIdentity.clear();
  }
  if (code && displayToken) {
    displayIdentity.set(code, displayToken);
    casinoDisplayIdentity.clear();
  }

  if (casinoParam || tableParam || (code && displayToken) || isGameType(gameParam)) {
    window.history.replaceState(null, '', window.location.pathname);
  }

  const saved = displayIdentity.get();
  const casinoSaved = casinoDisplayIdentity.get();
  return {
    game: preferredGameOnce,
    casino: casinoSaved.casino,
    table: casinoSaved.table,
    handoff:
      saved.code && saved.displayToken
        ? { code: saved.code, displayToken: saved.displayToken }
        : null,
  };
};

/** Test-only: the game memo survives between cases in one file. */
export const resetHostBootForTests = (): void => {
  preferredGameOnce = null;
};

/**
 * The TV is cast-only: it opens the room and mirrors the table, and that is
 * all it does. Every control — Deal, settings, kicks, grants — lives on the
 * phone of the player who took the first seat, with the casino owner's
 * console still outranking them. A TV never emits a host event.
 *
 * A cast receiver mounts this same component with an injected `boot` — the
 * URL the sender handed over arrived through the cast channel, not this
 * page's address — while a browser tab boots from its own URL query.
 */
export function Host({ boot: bootProp }: { boot?: HostBoot } = {}) {
  // This page is the felt. Its sound switch is the table's own, kept apart
  // from any player page the same browser may also hold — first statement,
  // before the Sound button or anything else reads the switch.
  setSoundScope('table');
  const [state, setState] = useState<RoomState | null>(null);
  const [closed, setClosed] = useState<string | null>(null);
  const [publicUrl, setPublicUrl] = useState<string>(() => window.location.origin);
  const [castAppId, setCastAppId] = useState<string | null>(null);
  const [displayToken, setDisplayToken] = useState<string | null>(
    () => displayIdentity.get().displayToken,
  );
  const [casino, setCasino] = useState<CasinoSnapshot | null>(null);
  const [leaderboard, setLeaderboard] = useState<CasinoLeaderboardEntry[] | null>(null);
  const [timeline, setTimeline] = useState<BalanceSeries[] | null>(null);
  /**
   * The window the house board quotes. A TV in a browser can tap it; the
   * pick rides localStorage so a phone that set Tonight sees Tonight here.
   */
  const [boardWindow, setBoardWindow] = useState<StatsWindow>(() =>
    parseStatsWindow(statsWindowPref.get()),
  );
  const changeBoardWindow = (window: StatsWindow) => {
    setBoardWindow(window);
    statsWindowPref.set(window);
  };
  const opening = useRef(false);
  const boot = useRef<HostBoot | null>(null);
  if (boot.current === null) boot.current = bootProp ?? readHostBoot();
  const preferredGame = useRef<GameType>(boot.current.game);
  const casinoCode = useRef<string | null>(boot.current.casino);
  const tableCode = useRef<string | null>(boot.current.table);
  const handoff = useRef(boot.current.handoff);
  const directory = casinoCode.current !== null && tableCode.current === null;
  /**
   * The code the public boards are read under. A private venue's snapshot
   * carries no join code, so a TV pinned to a table reads them under the
   * table's own code — the one thing this screen is certain to hold.
   */
  const boardCode =
    casinoCode.current ?? tableCode.current ?? (casino?.adhoc ? casino.joinCode : null);
  /**
   * A bare TV's table sits in a hidden venue of its own, so the model reads
   * it as a casino table: the join QR names venue and table, and a cast
   * pairs by the table code.
   */
  const pinnedTable = tableCode.current ?? (casino?.adhoc ? (state?.code ?? null) : null);

  // The felt reads the roll only once the dice have landed: while they are in
  // the air the model sees the world as it stood before the throw.
  const readState = useCrapsRoomReading(state);

  useWakeLock(state !== null || directory);
  useRouletteCues(state);
  // A pressed craps win stacks chips back on the number; the click waits for
  // the dice like the flash does. Every other game reads straight through.
  useTableCues(readState);
  useTableAmbience(state);
  useTvVolumes(state);
  useDealerCallouts(state);

  useEffect(() => {
    // `/api/config` fills in what the page cannot know on its own: the URL
    // other devices can reach (only localhost needs the rescue) and the
    // registered Cast App ID that turns on native casting.
    fetch('/api/config')
      .then((r) => r.json())
      .then((c: { publicUrl?: string; castAppId?: string | null }) => {
        if (c.publicUrl && !/localhost|127\.0\.0\.1/.test(c.publicUrl)) setPublicUrl(c.publicUrl);
        setCastAppId(c.castAppId ?? null);
      })
      .catch(() => undefined);
  }, []);

  const openRoom = useCallback(async () => {
    if (opening.current) return;
    opening.current = true;
    const socket = getSocket();
    try {
      const table = tableCode.current;
      if (table) {
        const res = await request<{ roomCode: string; casino: CasinoSnapshot }>((cb) =>
          socket.emit('tv_join_table', { code: table }, cb),
        );
        setCasino(res.casino);
        // A private venue pairs without a code: keep whatever identity this
        // tab already had rather than blanking it with the redacted answer.
        if (res.casino.joinCode) {
          casinoCode.current = res.casino.joinCode;
          casinoDisplayIdentity.set(res.casino.joinCode, table);
        }
        setClosed(null);
        return;
      }
      const code = casinoCode.current;
      if (code) {
        setCasino(await lookupCasino(code));
        setClosed(null);
        return;
      }
      const saved = handoff.current ?? displayIdentity.get();
      if (saved && saved.code && saved.displayToken) {
        try {
          const res = await request<CreateRoomResult>((cb) =>
            socket.emit('display_reclaim_room', { code: saved.code!, displayToken: saved.displayToken! }, cb),
          );
          setCasino(res.casino ?? null);
          displayIdentity.set(res.code, res.displayToken);
          setDisplayToken(res.displayToken);
          setClosed(null);
          return;
        } catch {
          displayIdentity.clear(); // Room expired while the TV was away.
          handoff.current = null;
        }
      }
      const res = await request<CreateRoomResult>((cb) =>
        socket.emit('display_create_room', { game: preferredGame.current }, cb),
      );
      setCasino(res.casino ?? null);
      displayIdentity.set(res.code, res.displayToken);
      setDisplayToken(res.displayToken);
      setClosed(null);
    } catch {
      // The retry affordance below re-runs openRoom; nothing else to do here.
    } finally {
      opening.current = false;
    }
  }, []);

  useEffect(() => {
    const socket = getSocket();
    const onState = (next: RoomState) => {
      setState(next);
      setClosed(null);
    };
    const onCasino = (next: CasinoSnapshot) => setCasino(next);
    const onClosed = ({ reason }: { reason: string }) => {
      if (tableCode.current) {
        setClosed(reason);
        setState(null);
        getSocket().emit('tv_join_table', { code: tableCode.current }, () => undefined);
        return;
      }
      setState(null);
      setClosed(reason);
      // A bare TV's venue closed with its table; the next one brings its own.
      setCasino(null);
      displayIdentity.clear();
      setDisplayToken(null);
      handoff.current = null;
    };
    const onConnect = () => {
      void openRoom();
    };

    socket.on('room_state', onState);
    socket.on('casino_state', onCasino);
    socket.on('room_closed', onClosed);
    socket.on('connect', onConnect);
    if (socket.connected) void openRoom();

    return () => {
      socket.off('room_state', onState);
      socket.off('casino_state', onCasino);
      socket.off('room_closed', onClosed);
      socket.off('connect', onConnect);
    };
  }, [openRoom]);

  /**
   * The board is public, so the TV reads it straight over REST. It only ever
   * changes when a round settles, so refetch on the round counter — never on
   * a timer. The timeline rides along so each desk gets its sparkline.
   */
  useEffect(() => {
    const code = boardCode;
    if (!code) return;
    let stale = false;
    Promise.all([fetchLeaderboard(code, boardWindow), fetchBalanceTimeline(code, boardWindow)])
      .then(([rows, night]) => {
        if (stale) return;
        setLeaderboard(Array.isArray(rows) ? rows : []);
        setTimeline(Array.isArray(night?.series) ? night.series : []);
      })
      .catch(() => undefined);
    return () => {
      stale = true;
    };
  }, [boardCode, boardWindow, state?.round, state?.phase === 'payout']);

  // Off the held reading, so a seven-out's sweep waits for the dice to land.
  useCrapsCues(readState);

  // Every label, line and seat plate below is decided by the model.
  const vm = useMemo(
    () =>
      hostModel(readState, {
        casino,
        casinoCode: casinoCode.current,
        tableCode: pinnedTable,
        publicUrl,
        displayToken,
        closed,
        leaderboard,
        timeline,
      }),
    [readState, casino, pinnedTable, publicUrl, displayToken, closed, leaderboard, timeline],
  );
  useWinCues(readState !== null, vm.winHeadline);
  // An injected boot means this Host is the one running ON the cast device,
  // mounted by the receiver app. Loading the sender framework there would put
  // a second copy of `window.cast.framework` on a page that already runs the
  // receiver half, so both cast routes stay dark and the button is gone.
  // An iPhone or Mac Safari is already the mirrored screen — Cast and its
  // "Mirror this screen instead" note would only clutter the Apple TV.
  const onReceiver = bootProp !== undefined;
  const hideCast = onReceiver || applePlatform() !== null;
  // Native Cast when CAST_APP_ID made it to /api/config; Presentation API
  // otherwise. A receiver-mounted or Apple-mirrored Host drives neither.
  const cast = useSenderCast(hideCast ? null : vm.castUrl, hideCast ? null : castAppId);

  // The TV page on a portrait phone is the iPhone cast path: draw landscape
  // and turn, so the mirrored screen shows the table, not a letterboxed column.
  const viewport = useViewportSize();
  const mirror = mirrorLayout(viewport.width, viewport.height);
  const hues = useMemo(() => seatHues(state), [state]);
  // A game switch replaces every picture on the screen at once; the card
  // names what is coming while it happens. The TV's is not dismissible —
  // there is nobody at the screen to tap it away.
  const { cue: splash } = useGameSplash(state?.gameType ?? null, GAME_SPLASH_MS);

  return (
    <div
      className={mirror.rotate ? styles.mirrorStage : undefined}
      style={mirror.rotate ? { width: mirror.width, height: mirror.height } : undefined}
    >
      <main
        className={mirror.rotate ? `${styles.table} ${styles.tableMirrored}` : styles.table}
        style={themeStyle(casino?.theme)}
      >
      <header className={styles.header}>
        <div>
          <p className={styles.kicker}>{vm.kicker}</p>
          <h1 className={styles.brand}>
            <CouchIcon className={styles.brandIcon} />TV Casino Night
          </h1>
        </div>
        <div className={styles.headerRight}>
          {vm.hostName && (
            <div className={styles.hostPill}>
              <span className={styles.hostPillLabel}>Host</span>
              <span className={styles.hostPillName}>
                {vm.hostName}
                {vm.hostHasProfile && <ProfileBadge />}
              </span>
            </div>
          )}
          <PartyPill party={state?.party} />
          <div className={styles.shoe}>{vm.seatedCount}/{vm.maxSeats} seats</div>
          <SoundButton remote={state?.soundOn} />
          {!hideCast && <CastButton cast={cast} blockedReason={vm.castBlocked} />}
          {vm.joinCodeLabel && (
            <div className={styles.joinChip}>
              <div className={styles.joinChipInfo}>
                <p className={styles.joinLabel}>Join at</p>
                <p className={styles.code}>{vm.joinCodeLabel}</p>
                <p className={styles.joinChipUrl}>{vm.joinUrlShort}</p>
              </div>
              <div className={`${styles.qr} ${styles.qrSmall}`}>
                <QRCodeSVG value={vm.joinUrl} size={84} bgColor="#f5efe2" fgColor="#06231a" level="M" />
              </div>
            </div>
          )}
        </div>
      </header>

      {vm.banner && <p className={styles.banner}>{vm.banner}</p>}

      {splash && (
        <GameSplash
          key={splash.key}
          vm={gameSplashVM(splash.game, { from: splash.from, rule: vm.ruleLine })}
          holdMs={GAME_SPLASH_MS}
        />
      )}

      {!state && directory && vm.lobbyBoard ? (
        <section className={styles.body}>
          <div className={`${styles.lobbyBand} ${styles.lobbyBandForced}`}>
            <LobbyBoard
              board={vm.lobbyBoard}
              stage
              statsWindow={boardWindow}
              onStatsWindow={changeBoardWindow}
            />
          </div>
        </section>
      ) : !state ? (
        <section className={styles.center}>
          <p className={styles.dim}>Opening the table…</p>
          <button className={styles.button} onClick={() => void openRoom()}>
            Retry
          </button>
        </section>
      ) : (
        <section className={styles.body}>
          {/* A live vote is the thing the room is doing; it sits above the
              felt and the lobby band both. */}
          {vm.gamePoll && <GamePollBoard vm={vm.gamePoll} />}

          {(vm.showJoinCard || vm.lobbyBoard) && (
            <div
              className={[
                styles.lobbyBand,
                vm.stageLobby ? styles.lobbyBandForced : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {vm.showJoinCard && (
                <aside className={styles.joinCard}>
                  <div className={styles.joinCardInfo}>
                    <p className={styles.joinLabel}>Join at</p>
                    <p className={styles.joinUrl}>{vm.joinUrlShort}</p>
                    <p className={styles.code}>{vm.joinCodeLabel}</p>
                    <p className={styles.dim}>Scan, or type the code on your phone.</p>
                  </div>
                  <div className={styles.qr}>
                    <QRCodeSVG value={vm.joinUrl} size={220} bgColor="#f5efe2" fgColor="#06231a" level="M" />
                  </div>
                </aside>
              )}

              {vm.lobbyBoard && (
                <LobbyBoard
                  board={vm.lobbyBoard}
                  stage={vm.stageLobby}
                  statsWindow={boardWindow}
                  onStatsWindow={changeBoardWindow}
                />
              )}
            </div>
          )}

          {vm.stageLobby && (
            <p className={styles.stageStatus}>{vm.statusLine}</p>
          )}

          {vm.showFelt && (
          <div className={styles.tableArea}>
            {vm.winHeadline && <WinBanner key={vm.winHeadline.key} headline={vm.winHeadline} />}
            {state.calloutMode !== 'off' && <CalloutCaption />}
            {vm.shoe ? (
              <DeckShoe vm={vm.shoe} className={styles.deckCorner} />
            ) : (
              !vm.historyLabel && <span className={styles.shoeBox}>{vm.shoeLabel}</span>
            )}

            <div className={styles.feltRow}>
            {vm.roulette?.board && (
              <PocketBoard board={vm.roulette.board} road={vm.roulette.pocketRoad} count={vm.historyLabel} />
            )}
            <Felt fill={vm.isKeno}>
              {vm.isRoulette && vm.roulette ? (
                <SeatHuesContext.Provider value={hues}>
                  <RouletteFelt vm={vm.roulette} endsAt={state.deadline?.endsAt ?? null} />
                </SeatHuesContext.Provider>
              ) : vm.isCraps && vm.craps ? (
                <SeatHuesContext.Provider value={hues}>
                  <CrapsFelt vm={vm.craps} />
                </SeatHuesContext.Provider>
              ) : vm.isKeno && vm.keno ? (
                <KenoTable view={vm.keno} endsAt={state.deadline?.endsAt ?? null} />
              ) : vm.dealerRow.baccarat && vm.dealerRow.coupHands ? (
                <div className={styles.coup}>
                  {vm.dealerRow.coupHands.map((hand) => (
                    <div
                      key={hand.spot}
                      className={[
                        styles.coupHand,
                        styles[`spot_${hand.spot}`] ?? '',
                        hand.won ? styles.coupHandWon : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      <span className={styles.dealerLabel}>{hand.label}</span>
                      {hand.cardsFaceUp.length > 0 ? (
                        <>
                          <CardRow cards={hand.cardsFaceUp} large />
                          <span className={styles.dealerValue}>{hand.total}</span>
                        </>
                      ) : (
                        <div className={styles.dealerSlot}>{hand.emptyLabel}</div>
                      )}
                    </div>
                  ))}
                </div>
              ) : vm.dealerRow.holdem ? (
                <>
                  <span className={styles.dealerLabel}>{vm.dealerRow.label}</span>
                  {vm.dealerRow.cardsFaceUp.length > 0 ? (
                    <CardRow cards={vm.dealerRow.cardsFaceUp} large />
                  ) : (
                    <div className={styles.dealerSlot}>{vm.dealerRow.emptyLabel}</div>
                  )}
                  {vm.dealerRow.potLabel && (
                    <span className={styles.potPlate}>{vm.dealerRow.potLabel}</span>
                  )}
                </>
              ) : (
                <>
                  <span className={styles.dealerLabel}>{vm.dealerRow.label}</span>
                  {vm.dealerRow.cardsFaceUp.length > 0 ? (
                    <>
                      <CardRow cards={vm.dealerRow.cardsFaceUp} large />
                      <span className={styles.dealerValue}>{vm.dealerRow.valueLine}</span>
                    </>
                  ) : (
                    <div className={styles.dealerSlot}>{vm.dealerRow.emptyLabel}</div>
                  )}
                </>
              )}
            </Felt>
            </div>

            <svg className={styles.arcText} viewBox="0 0 600 84" aria-hidden="true">
              <defs>
                <path id="felt-arc" d="M 14 80 A 1095 1095 0 0 1 586 80" fill="none" />
              </defs>
              <text>
                <textPath href="#felt-arc" startOffset="50%" textAnchor="middle">
                  {vm.feltText}
                </textPath>
              </text>
            </svg>
            <p className={styles.ruleLine}>{vm.ruleLine}</p>
            {vm.insuranceLine && <p className={styles.insuranceLine}>{vm.insuranceLine}</p>}

            <ul
              className={[
                styles.seatArc,
                vm.isRoulette || vm.isCraps || vm.isKeno ? styles.seatArcFlat : '',
                vm.seatsClosedUp ? styles.seatArcClosed : '',
              ]
                .filter(Boolean)
                .join(' ')}
              data-seat-count={vm.shownSeatRows.length}
              style={{ '--seat-count': vm.shownSeatRows.length } as CSSProperties}
            >
              {vm.shownSeatRows.map((row, i, shown) => (
                <li
                  key={row.index}
                  className={[
                    styles.seat,
                    row.taken ? styles.seatTaken : styles.seatEmpty,
                    row.taken && !row.connected ? styles.seatOffline : '',
                    row.active ? styles.seatActive : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={
                    vm.seatsClosedUp
                      ? ({ '--arc-t': shown.length > 1 ? (i / (shown.length - 1)) * 2 - 1 : 0 } as CSSProperties)
                      : undefined
                  }
                >
                  {row.taken ? (
                    <>
                      {row.win && <WinBurst key={row.win.key} win={row.win} />}
                      <div className={styles.seatCards}>
                        {row.cardsFaceUp ? (
                          <div className={styles.seatHand}>
                            <CardRow cards={row.cardsFaceUp} overlap />
                            {row.mark !== '' && (
                              <span
                                key={row.markKey}
                                className={row.markPop ? `${styles.seatHandMark} ${styles.markPop}` : styles.seatHandMark}
                              >
                                {row.mark}
                              </span>
                            )}
                          </div>
                        ) : row.mark !== '' ? (
                          <span
                            key={row.markKey}
                            className={row.markPop ? `${styles.seatHandMark} ${styles.markPop}` : styles.seatHandMark}
                          >
                            {row.mark}
                          </span>
                        ) : row.hands ? (
                          <div className={styles.seatHands}>
                            {row.hands.map((hand, i) => (
                              <div key={i} className={styles.seatHand}>
                                <CardRow cards={hand.cardsFaceUp} overlap />
                                <span
                                  key={hand.key}
                                  className={
                                    hand.pop
                                      ? `${styles.seatHandMark} ${styles.markPop}`
                                      : styles.seatHandMark
                                  }
                                >
                                  {hand.mark}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className={styles.seatStatus}>{row.statusLabel}</span>
                        )}
                      </div>
                      <div
                        key={row.chipKey}
                        className={[
                          styles.betSpot,
                          row.committed > 0 ? styles.betSpotFilled : '',
                          row.bacSpot ? styles[`spot_${row.bacSpot}`] ?? '' : '',
                          row.chipMotion ? styles[`chip_${row.chipMotion}`] ?? '' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        {row.committed > 0 ? row.committed.toLocaleString() : 'Bet'}
                      </div>
                      {row.sideBets.length > 0 && (
                        <div className={styles.sideBets}>
                          {row.sideBets.map((bet, i) => (
                            <span
                              key={`${bet.name}-${i}`}
                              className={[styles.sideBetLine, styles[`sideBet_${bet.tone}`] ?? '']
                                .filter(Boolean)
                                .join(' ')}
                            >
                              <span className={styles.sideBetName}>{bet.name}</span>
                              <span className={styles.sideBetStake}>{bet.stake}</span>
                              {bet.mark && <span className={styles.sideBetMark}>{bet.mark}</span>}
                            </span>
                          ))}
                        </div>
                      )}
                      <div
                        className={styles.seatPlate}
                        style={
                          {
                            '--seat-hue': row.hue,
                            '--seat-hue-light': row.hueLight,
                            '--seat-hue-wash': row.hueWash,
                          } as CSSProperties
                        }
                      >
                        <span className={styles.seatWho}>
                          <span data-seat-dot={row.index} className={styles.seatAvatar} aria-hidden="true">
                            {row.initials}
                          </span>
                          <span className={styles.seatName}>
                            {row.badges.map((b) => (
                              <span
                                key={b}
                                className={
                                  b === 'D' ? `${styles.seatBadge} ${styles.seatBadgeButton}` : styles.seatBadge
                                }
                              >
                                {b}
                              </span>
                            ))}
                            {row.name}
                          </span>
                          {row.hasProfile && <ProfileBadge />}
                          {row.isHost && <span className={styles.runnerMark}>★</span>}
                        </span>
                        <span className={styles.seatChips}>
                          {row.chips.toLocaleString()} chips
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className={`${styles.betSpot} ${styles.betSpotOpen}`} />
                      <span className={styles.seatOpen}>Open seat</span>
                    </>
                  )}
                </li>
              ))}
            </ul>

            {/* The count leads the strip it numbers. Until a board has enough
                spins to stand beside the felt, the road rides the rail. */}
            {vm.historyLabel && !vm.roulette?.board && (
              <div className={styles.railHistory}>
                <span className={styles.historyCount}>{vm.historyLabel}</span>
                {vm.roulette && vm.roulette.pocketRoad.length > 0 && (
                  <PocketRoad road={vm.roulette.pocketRoad} />
                )}
                {vm.craps && vm.craps.rollRibbon.length > 0 && (
                  <ol className={styles.pocketRoad} aria-label="Recent rolls, oldest first">
                    {vm.craps.rollRibbon.map((r) => (
                      <li
                        key={r.key}
                        className={[styles.bead, styles.rollBead, r.hard ? styles.rollBeadHard : '']
                          .filter(Boolean)
                          .join(' ')}
                        title={`${r.dice[0]} and ${r.dice[1]}`}
                      >
                        {r.total}
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            )}

            {vm.craps && (vm.craps.stats.longest || vm.craps.stats.hot.length > 0) && (
              <div className={styles.crapsStats} aria-label="The night at the craps table">
                {vm.craps.stats.longest && (
                  <span className={styles.crapsStat}>
                    Longest hand · {vm.craps.stats.longest.rolls} rolls
                    {vm.craps.stats.longest.name ? ` · ${vm.craps.stats.longest.name}` : ''}
                  </span>
                )}
                {vm.craps.stats.hot.length > 0 && (
                  <span className={styles.crapsStat}>
                    Hot · {vm.craps.stats.hot.map((t) => `${t.total}×${t.count}`).join(' ')}
                  </span>
                )}
              </div>
            )}

            {vm.beadRoad.length > 0 && (
              <ol className={styles.beadRoad} aria-label="Past coups, oldest first">
                {vm.beadRoad.map((bead, i) => (
                  <li
                    key={`${i}-${bead.outcome}`}
                    className={`${styles.bead} ${styles[`spot_${bead.outcome}`] ?? ''}`}
                    title={bead.label}
                  >
                    {bead.letter}
                  </li>
                ))}
              </ol>
            )}

            {vm.threecardRoad.length > 0 && (
              <ol className={styles.beadRoad} aria-label="Past hands, oldest first">
                {vm.threecardRoad.map((bead) => (
                  <li key={bead.key} className={styles.bead} title={bead.label}>
                    {bead.letter}
                  </li>
                ))}
              </ol>
            )}

            <footer className={styles.actions}>
              <span className={styles.dim}>{vm.statusLine}</span>
              {vm.payoutNoteLine && <span className={styles.dim}>{vm.payoutNoteLine}</span>}
            </footer>
          </div>
          )}
        </section>
      )}
      </main>
    </div>
  );
}
